"use client";
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import Link from "next/link";
import * as Sentry from "@sentry/nextjs";
import api, { withRetry, isTransportError } from "@/lib/api";
import { identifyUser } from "@/lib/sentry-user";
import { auth } from "@/lib/firebase-client";
import { onAuthStateChanged, signOut, signInWithCustomToken } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { resolverPais } from "@/lib/geo/pais";
import { suggestEmail } from "@/lib/email-typo";
import { destinoSeguro } from "@/lib/destinoSeguro";
import RevokedNotice from "./RevokedNotice";
import { REVOKED_STORAGE_KEY, NO_PURCHASE_STORAGE_KEY } from "@/lib/api";

// Mapeo de error_code del backend -> clave de traducción a mostrar.
const ERROR_CODE_KEYS = {
  ONLY_BUYERS: "login_error_only_buyers",
  // ZeroBounce marcó el mail como no entregable: mismo mensaje que un bounce de SES.
  EMAIL_UNDELIVERABLE: "login_bounce_warning",
  // Mail suprimido (bounce permanente / queja / desuscripción): no se manda el código,
  // hay que contactarse para cambiar el email.
  EMAIL_SUPPRESSED: "login_suppressed_warning",
  // Cooldown del backend (429): pidió demasiados códigos seguidos. Lleva {{minutes}}.
  TOO_MANY_CODES: "login_too_many_codes",
};

// Mismo número que figura en el aviso legal (+54 9 11 7372-8515).
const WHATSAPP_NUMBER = "5491173728515";
const SUPPORT_EMAIL = "contacto@briancallimusic.com";

export default function HomeLogin() {
  const { t, i18n } = useTranslation();
  const search = useSearchParams();
  const router = useRouter();

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [hasBounces, setHasBounces] = useState(false);
  // Acceso revocado por distribución no autorizada: los datos del cartel (mail, caso,
  // fecha) vienen del 403 del backend. Mientras esté seteado, el cartel reemplaza todo.
  const [revoked, setRevoked] = useState(null);
  // Corrección sugerida del dominio cuando el mail rebotó por ONLY_BUYERS.
  const [suggestion, setSuggestion] = useState("");
  // El mail de la sesión que cerramos por no tener compras. Sirve para que el mensaje de
  // WhatsApp diga con qué cuenta estaba entrando, que es el dato que Brian necesita para
  // encontrar la compra.
  const [sesionPrevia, setSesionPrevia] = useState("");

  // OTP state
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!auth) return;
    let redirected = false;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user && !redirected) {
        try {
          redirected = true;
          await user.getIdToken(true);
          router.replace(destinoSeguro(search.get("continue")));
        } catch (e) {
          redirected = false;
          console.error("[login] Token refresh failed, signing out:", e);
          await signOut(auth);
        }
      }
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuenta revocada mientras la persona ya estaba adentro: el interceptor de la API la
  // desloguea, deja los datos del cartel en sessionStorage y la manda acá. Se lee (y se
  // limpia) en un efecto y no en el estado inicial para no romper la hidratación.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(REVOKED_STORAGE_KEY);
      if (raw) {
        sessionStorage.removeItem(REVOKED_STORAGE_KEY);
        setRevoked(JSON.parse(raw));
        return;
      }
    } catch {
      /* sessionStorage bloqueado o JSON roto: se muestra el login normal */
    }

    // La sesión que traía no era de un comprador. Acá NO va un cartel que se coma la
    // pantalla como el de la cuenta revocada: lo que esta persona tiene que hacer es
    // entrar con OTRO mail, así que el formulario tiene que quedar a la vista. Sí hay
    // que decirle cuál cuenta cerramos, porque si no el deslogueo parece un error más.
    try {
      const raw = sessionStorage.getItem(NO_PURCHASE_STORAGE_KEY);
      if (!raw) return;
      sessionStorage.removeItem(NO_PURCHASE_STORAGE_KEY);
      const previo = String(JSON.parse(raw)?.email || "");
      setSesionPrevia(previo);
      // Sólo el estado: el TEXTO se arma en el render. Al montar, i18n todavía puede no
      // estar inicializado y t() devolvería la clave cruda, que es lo que vería la
      // persona. En el render se recalcula solo cuando el idioma termina de cargar.
      setErrorCode("NO_PURCHASE");
      // El input queda VACÍO a propósito: precargarle el mail que acaba de rebotar sería
      // invitarlo a mandar el mismo de nuevo. Lo que sí ofrecemos es la corrección de
      // dominio, por si el problema fue un typo al crear la cuenta.
      if (previo) {
        const fix = suggestEmail(previo);
        setSuggestion(fix && fix !== previo.toLowerCase() ? fix : "");
      }
    } catch {
      /* sessionStorage bloqueado o JSON roto: se muestra el login normal */
    }
  }, []);

  // Focus first OTP input when code is sent
  useEffect(() => {
    if (codeSent && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [codeSent]);

  // Pedido del código. `override` viene del botón "probar con el mail corregido": el
  // estado de React todavía no se actualizó cuando se dispara, así que el valor va
  // explícito en vez de leerse de `email`.
  const sendCode = async (override) => {
    const target = String(override ?? email).trim();
    if (!target) return;

    setErrorMsg("");
    setErrorCode("");
    setSuggestion("");
    setSubmitting(true);

    try {
      const lang = (i18n.language || "es").split("-")[0];
      // País para la lista de mails: si el gate lo rechaza por no-comprador, el backend
      // lo guarda en newsletter_subscribers y ahí el país define el huso del goteo.
      const cc = resolverPais({ fallback: "" }) || undefined;
      identifyUser(target);
      // NO reintentar: send-code no es idempotente (cada llamada invalida el
      // codigo anterior y manda un mail nuevo). Un ERR_NETWORK no garantiza que la
      // request no llego, asi que reintentar podria duplicar el email.
      const { data } = await api.post("/api/auth/send-code", { email: target, lang, cc });
      setHasBounces(!!data.has_bounces);
      setCodeSent(true);
    } catch (err) {
      const data = err?.response?.data || {};
      const code = data.error_code || "";
      setErrorCode(code);

      // Acceso revocado: no hay nada que reintentar ni ningún mensaje que mostrar en
      // el formulario. El cartel se come la pantalla entera.
      if (code === "ACCESS_REVOKED") {
        setRevoked(data.revoked || {});
        return;
      }

      // "No sos comprador" es, 1 de cada 4 veces, un comprador que tecleó mal el
      // dominio. Le ofrecemos la corrección en vez de mandarlo a comprar de nuevo.
      if (code === "ONLY_BUYERS") {
        const fix = suggestEmail(target);
        setSuggestion(fix && fix !== target.toLowerCase() ? fix : "");
      }

      const translationKey = ERROR_CODE_KEYS[code];
      const msg = translationKey
        ? t(translationKey, { minutes: data.retry_after_minutes ?? 15 })
        : isTransportError(err)
        ? t("network_error")
        : data.error || data.message || err?.message || t("send_code_failed");
      setErrorMsg(msg);

      if (!code) {
        Sentry.captureException(err, {
          tags: { flow: "passwordless_login", step: "send_code" },
          extra: {
            email: target,
            http_status: err?.response?.status,
            response_data: data,
          },
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitEmail = async (e) => {
    e.preventDefault();
    await sendCode();
  };

  // Acepta la sugerencia: reemplaza lo tecleado y reintenta con el mail corregido.
  const useSuggestion = async () => {
    const fixed = suggestion;
    setEmail(fixed);
    await sendCode(fixed);
  };

  const handleDigitChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    // Auto-advance to next input
    if (digit && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 4 digits are filled
    if (digit && index === 3 && newDigits.every((d) => d !== "")) {
      submitCode(newDigits);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!pasted) return;
    const newDigits = ["", "", "", ""];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setDigits(newDigits);
    // Focus last filled or next empty
    const focusIdx = Math.min(pasted.length, 3);
    inputRefs.current[focusIdx]?.focus();
    // Auto-submit if 4 digits pasted
    if (pasted.length === 4) {
      submitCode(newDigits);
    }
  };

  const submitCode = async (codeDigits) => {
    const code = codeDigits.join("");
    if (code.length !== 4) return;

    setErrorMsg("");
    setVerifying(true);

    try {
      identifyUser(email.trim());
      // NO reintentar verify-code: el backend marca el codigo como usado ANTES de
      // responder, asi que ante un ERR_NETWORK con la respuesta perdida (la request
      // SI llego) el 2do intento daria 422 y dejaria afuera a un usuario con el
      // codigo correcto. Mejor mostrar el mensaje de red y que reintente a mano.
      const { data } = await api.post("/api/auth/verify-code", { email: email.trim(), code });

      if (data.customToken && auth) {
        // El custom token es reutilizable hasta expirar y NO muta estado del
        // backend, asi que reintentar SOLO el canje ante un network-request-failed
        // de Firebase es seguro.
        await withRetry(
          () => signInWithCustomToken(auth, data.customToken),
          { retries: 1, retryOn: (e) => e?.code === "auth/network-request-failed" },
        );
        router.replace(destinoSeguro(search.get("continue")));
      }
    } catch (err) {
      const data = err?.response?.data || {};
      const firebaseCode = err?.code;
      const isHttp422Invalid = data.error === "invalid_or_expired_code";

      // Revocada entre el pedido del código y el verify (el código vive 10 minutos).
      if (data.error_code === "ACCESS_REVOKED") {
        setRevoked(data.revoked || {});
        return;
      }

      // Cooldown del backend: demasiados códigos equivocados. Los vivos se vencieron,
      // así que pasada la espera tiene que pedir uno nuevo.
      const isCooldown = data.error_code === "TOO_MANY_ATTEMPTS";

      let msg;
      if (isHttp422Invalid) {
        msg = t("code_invalid_or_expired");
      } else if (isCooldown) {
        msg = t("login_too_many_attempts", { minutes: data.retry_after_minutes ?? 15 });
      } else if (isTransportError(err) || firebaseCode === "auth/network-request-failed") {
        msg = t("network_error");
      } else if (firebaseCode) {
        msg = `${t("code_verify_failed")} (${firebaseCode})`;
      } else {
        msg = data.error || data.message || t("code_verify_failed");
      }
      setErrorMsg(msg);
      setDigits(["", "", "", ""]);
      inputRefs.current[0]?.focus();

      if (!isHttp422Invalid && !isCooldown) {
        Sentry.captureException(err, {
          tags: { flow: "passwordless_login", step: "verify_code" },
          extra: {
            email: email.trim(),
            firebase_code: firebaseCode,
            http_status: err?.response?.status,
            response_data: data,
          },
        });
      }
    } finally {
      setVerifying(false);
    }
  };

  const onSubmitCode = (e) => {
    e.preventDefault();
    submitCode(digits);
  };

  const resendCode = async () => {
    setErrorMsg("");
    setSubmitting(true);
    try {
      const lang = (i18n.language || "es").split("-")[0];
      // NO reintentar (mismo motivo que onSubmitEmail: evitar mail duplicado).
      const { data } = await api.post("/api/auth/send-code", { email: email.trim(), lang });
      setHasBounces(!!data.has_bounces);
      setDigits(["", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (err) {
      const data = err?.response?.data || {};
      const code = data.error_code || "";
      const translationKey = ERROR_CODE_KEYS[code];
      setErrorCode(code);
      if (code === "ACCESS_REVOKED") {
        setRevoked(data.revoked || {});
        return;
      }
      setErrorMsg(
        translationKey
          ? t(translationKey, { minutes: data.retry_after_minutes ?? 15 })
          : isTransportError(err)
          ? t("network_error")
          : data.error || data.message || t("send_code_failed"),
      );
      if (!code) {
        Sentry.captureException(err, {
          tags: { flow: "passwordless_login", step: "resend_code" },
          extra: {
            email: email.trim(),
            http_status: err?.response?.status,
            response_data: data,
          },
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // --- Cuenta revocada: se come la pantalla, no hay formulario que mostrar ---
  if (revoked) {
    return (
      <RevokedNotice
        email={revoked.email || email.trim()}
        caseCode={revoked.case}
        revokedAt={revoked.revokedAt}
        note={revoked.note}
        reason={revoked.reason}
      />
    );
  }

  // NO_PURCHASE es el mismo callejón que ONLY_BUYERS visto desde adentro de la sesión, así
  // que le sirven las mismas salidas: la corrección de dominio, el WhatsApp, y el recordá
  // que capaz compraste con el mail de Apple Pay. La única diferencia es el mensaje.
  const isOnlyBuyers = errorCode === "ONLY_BUYERS" || errorCode === "NO_PURCHASE";
  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    t("login_help_whatsapp_msg", { email: email.trim() || sesionPrevia }),
  )}`;

  // El aviso de "te cerré la sesión porque esa cuenta no tiene compras" se traduce acá,
  // en el render, y no en el efecto que lo detecta (ver el comentario de allá arriba).
  // `errorMsg` gana si existe: ese viene de un envío que la persona acaba de hacer, y es
  // más nuevo que el motivo por el que llegó a esta pantalla.
  const mensajeError =
    errorMsg ||
    (errorCode === "NO_PURCHASE"
      ? sesionPrevia
        ? t("login_session_no_purchase", { email: sesionPrevia })
        : t("login_error_only_buyers")
      : "");

  // --- OTP screen ---
  if (codeSent) {
    return (
      <div className="container position-relative pt-130 pb-60">
        <main className="main-rows">
          <div className="row mainRow" style={{ background: "#fff" }}>
            <div className="col-sm-12 py-5">
              <h3 className="text-center font-bold text-black mb-4">
                {t("code_enter_title")}
              </h3>
              <p className="text-center mb-2" style={{ color: "#555" }}>
                {t("code_sent_to")} <strong>{email}</strong>
              </p>
              <p className="text-center mb-4" style={{ color: "#888", fontSize: 14 }}>
                {t("code_check_spam")}
              </p>

              <form onSubmit={onSubmitCode} className="mx-auto" style={{ maxWidth: 420 }}>
                <div className="d-flex justify-content-center gap-3 mb-4">
                  {digits.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className="form-control text-center"
                      style={{
                        width: 64,
                        height: 72,
                        fontSize: 32,
                        fontWeight: 700,
                        fontFamily: "monospace",
                        borderRadius: 12,
                      }}
                      value={digit}
                      onChange={(e) => handleDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(i, e)}
                      onPaste={i === 0 ? handlePaste : undefined}
                      disabled={verifying}
                      autoComplete="one-time-code"
                    />
                  ))}
                </div>

                {errorMsg && (
                  <div className="alert alert-danger mb-3">{errorMsg}</div>
                )}

                {hasBounces && (
                  <div className="alert alert-warning mb-3">
                    {t("login_bounce_warning")}
                  </div>
                )}

                <div className="align-center mb-3">
                  <button
                    type="submit"
                    className="btn btn-mod btn-border btn-circle btn-large"
                    disabled={verifying || digits.some((d) => d === "")}
                  >
                    {verifying ? t("code_verifying") : t("code_verify_btn")}
                  </button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    className="btn btn-link"
                    style={{ color: "#888", fontSize: 14 }}
                    onClick={resendCode}
                    disabled={submitting}
                  >
                    {submitting ? t("code_resending") : t("code_resend")}
                  </button>
                  <span style={{ color: "#ccc", margin: "0 8px" }}>|</span>
                  <button
                    type="button"
                    className="btn btn-link"
                    style={{ color: "#888", fontSize: 14 }}
                    onClick={() => {
                      setCodeSent(false);
                      setDigits(["", "", "", ""]);
                      setErrorMsg("");
                      setErrorCode("");
                      setHasBounces(false);
                    }}
                  >
                    {t("code_change_email")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // --- Email screen ---
  return (
    <div className="container position-relative pt-130 pb-60">
      <main className="main-rows">
        <div className="row mainRow" style={{ background: "#fff" }}>
          <div className="col-sm-12 py-5">
            <h3 className="text-center font-bold text-black mb-2">
              {t("login_title")}
            </h3>
            {/* El acceso es solo para compradores: decirlo acá evita el ONLY_BUYERS
                de quien prueba con otro mail sin saber que tiene que ser el de la compra. */}
            <p className="text-center mb-4" style={{ color: "#555" }}>
              {t("login_subtitle")}
            </p>

            <form onSubmit={onSubmitEmail} className="mx-auto" style={{ maxWidth: 420 }}>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  {t("email_label")}
                </label>
                <input
                  id="email"
                  type="email"
                  className="form-control"
                  placeholder={t("email_placeholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              {mensajeError && (
                <>
                  <div className="alert alert-danger mb-3">{mensajeError}</div>

                  {/* Salidas para el que SÍ compró: 1 de cada 4 bloqueados por ONLY_BUYERS
                      termina entrando con otro mail a los pocos minutos (typo de dominio o
                      compró con el mail de Apple Pay / de la pareja). Antes de acá solo veía
                      "comprá tu libro", que para ese caso es una pared. */}
                  {isOnlyBuyers && (
                    <div className="mb-4">
                      {suggestion && (
                        <div className="alert alert-info mb-3 text-center">
                          <div className="mb-2">
                            {t("login_typo_question")}{" "}
                            {/* En pantallas angostas el mail no entra en una línea. Con <wbr>
                                antes de la arroba el corte cae ahí y el dominio queda entero,
                                que es justo lo que el usuario tiene que leer para decidir. */}
                            <strong style={{ overflowWrap: "anywhere" }}>
                              {suggestion.slice(0, suggestion.lastIndexOf("@"))}
                              <wbr />
                              {suggestion.slice(suggestion.lastIndexOf("@"))}
                            </strong>
                            {t("login_typo_question_end")}
                          </div>
                          <button
                            type="button"
                            className="btn btn-mod btn-border btn-circle btn-small"
                            onClick={useSuggestion}
                            disabled={submitting}
                          >
                            {t("login_typo_use")}
                          </button>
                        </div>
                      )}

                      <p className="mb-2" style={{ color: "#555", fontSize: 14 }}>
                        {t("login_other_email_hint")}
                      </p>

                      <div className="text-center mb-2">
                        <a
                          href={waHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-mod btn-circle btn-small"
                          style={{ background: "#25D366", borderColor: "#25D366", color: "#fff" }}
                        >
                          <i className="fa-whatsapp me-2" aria-hidden="true" />
                          {t("login_help_whatsapp")}
                        </a>
                      </div>

                      <p className="text-center mb-4" style={{ color: "#888", fontSize: 13 }}>
                        {t("login_help_email")}{" "}
                        <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#888" }}>
                          {SUPPORT_EMAIL}
                        </a>
                      </p>

                      <hr style={{ opacity: 0.15 }} />

                      <p className="text-center mb-2" style={{ color: "#555", fontSize: 14 }}>
                        {t("login_not_bought_yet")}
                      </p>
                      <div className="text-center">
                        <Link href={`/${i18n.language?.split("-")[0] || "es"}/libros`}>
                          <button
                            type="button"
                            className="btn btn-mod btn-border btn-circle btn-small"
                          >
                            {t("buy_button")}
                          </button>
                        </Link>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="align-center">
                <button
                  type="submit"
                  className="btn btn-mod btn-border btn-circle btn-large"
                  disabled={submitting}
                >
                  {submitting ? t("login_btn_loading") : t("login_btn")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
